import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, Eye, EyeSlash, CheckCircle } from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';
import { HaleWordmark } from '../../components/HaleBrand';
import TI from '../../components/brand/TI';
import HaleHead from '../../components/hale/HaleHead';
import { T, FONT } from './_theme';

/* /hale/login — fully white-labeled Hale Brand Portal sign in.
 * Reuses the existing ToxIntel auth backend via useAuth(); no duplicate
 * auth system. Includes inline Forgot Password (no ToxIntel screens). */

const inputCls = 'w-full h-11 px-3.5 rounded-lg border text-[14px] outline-none transition-colors focus:border-[#E63946]';
const inputStyle = { borderColor: T.LINE, backgroundColor: T.WHITE, color: T.INK, fontFamily: FONT.BODY };

export default function HaleLogin() {
  const { login, forgotPassword, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState('login'); // 'login' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const target = location.state?.from?.pathname || '/hale/dashboard';

  // Hale staff (role hale_admin) operate the marketplace → Hale Console.
  // Internal Agyekum/ToxIntel admins → ToxIntel Assessor Center for Hale.
  // Brand/demo users → the Hale brand workspace.
  const destFor = (u) => {
    if (u?.role === 'hale_admin') return '/hale/console';
    if (u?.role === 'admin') return '/toxintel/assessor/hale';
    return target;
  };

  useEffect(() => {
    if (!authLoading && user) navigate(destFor(user), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, target, navigate]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    const r = await login(email, password);
    if (r.success) navigate(destFor(r.user), { replace: true });
    else setError(r.error || 'Invalid email or password.');
    setBusy(false);
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    const r = await forgotPassword(email);
    if (r.success) setSent(true);
    else setError(r.error || 'Could not send reset link.');
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: T.WHITE, color: T.INK, fontFamily: FONT.BODY }}>
      <HaleHead title="Hale Brand Portal" />
      <header className="px-5 sm:px-8 h-16 flex items-center border-b" style={{ borderColor: T.LINE }}>
        <Link to="/" aria-label="Hale home"><HaleWordmark size="sm" /></Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <p className="text-[10px] uppercase tracking-[0.24em] mb-3" style={{ color: T.RED, fontFamily: FONT.MONO }}>The Hale League</p>
            <h1 className="text-[clamp(1.9rem,4vw,2.6rem)] font-black tracking-[-0.02em] leading-[1.05]" style={{ fontFamily: FONT.DISPLAY, color: T.INK }} data-testid="hale-login-title">
              Hale Brand Portal
            </h1>
            <p className="mt-3 text-[14px] leading-relaxed" style={{ color: T.MUTED }} data-testid="hale-login-subtitle">
              Access your Hale application, scientific review status, documents, and marketplace resources.
            </p>
          </div>

          {error && (
            <div className="mb-5 px-4 py-3 rounded-lg text-[13px]" style={{ backgroundColor: 'rgba(185,28,28,0.06)', color: '#B91C1C', border: '1px solid rgba(185,28,28,0.25)' }} data-testid="hale-login-error">
              {error}
            </div>
          )}

          {mode === 'login' ? (
            <form onSubmit={submitLogin} className="space-y-4" data-testid="hale-login-form">
              <div>
                <label className="block text-[12px] font-semibold mb-1.5" style={{ color: T.INK }}>Email</label>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} style={inputStyle} placeholder="you@brand.com" data-testid="hale-login-email" />
              </div>
              <div>
                <label className="block text-[12px] font-semibold mb-1.5" style={{ color: T.INK }}>Password</label>
                <div className="relative">
                  <input type={showPw ? 'text' : 'password'} required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} style={inputStyle} placeholder="••••••••" data-testid="hale-login-password" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: T.MUTED }} tabIndex={-1}>
                    {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={busy} className="w-full h-11 rounded-lg text-[13px] font-semibold tracking-wide inline-flex items-center justify-center gap-2 disabled:opacity-60" style={{ backgroundColor: T.RED, color: T.WHITE }} data-testid="hale-login-submit">
                {busy ? 'Signing in…' : <>Sign In <ArrowRight size={15} weight="bold" /></>}
              </button>
              <div className="flex items-center justify-between pt-1">
                <button type="button" onClick={() => { setMode('forgot'); setError(''); }} className="text-[12.5px] font-medium" style={{ color: T.MUTED }} data-testid="hale-login-forgot">
                  Forgot password?
                </button>
                <Link to="/hale/register" className="text-[12.5px] font-semibold" style={{ color: T.RED }} data-testid="hale-login-create">
                  Create Account →
                </Link>
              </div>
            </form>
          ) : sent ? (
            <div className="space-y-4" data-testid="hale-forgot-sent">
              <div className="flex items-center gap-2 px-4 py-3 rounded-lg" style={{ backgroundColor: 'rgba(22,163,74,0.08)', color: '#15803D' }}>
                <CheckCircle size={18} weight="fill" /> <span className="text-[13px]">If an account exists for {email}, a reset link is on its way.</span>
              </div>
              <button onClick={() => { setMode('login'); setSent(false); }} className="text-[12.5px] font-semibold" style={{ color: T.RED }}>← Back to sign in</button>
            </div>
          ) : (
            <form onSubmit={submitForgot} className="space-y-4" data-testid="hale-forgot-form">
              <div>
                <label className="block text-[12px] font-semibold mb-1.5" style={{ color: T.INK }}>Email</label>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} style={inputStyle} placeholder="you@brand.com" data-testid="hale-forgot-email" />
              </div>
              <button type="submit" disabled={busy} className="w-full h-11 rounded-lg text-[13px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" style={{ backgroundColor: T.RED, color: T.WHITE }} data-testid="hale-forgot-submit">
                {busy ? 'Sending…' : 'Send reset link'}
              </button>
              <button type="button" onClick={() => { setMode('login'); setError(''); }} className="text-[12.5px] font-semibold" style={{ color: T.MUTED }}>← Back to sign in</button>
            </form>
          )}

          <p className="mt-10 text-[10px] uppercase tracking-[0.18em] text-center" style={{ color: T.MUTED, fontFamily: FONT.MONO }} data-testid="hale-login-footer">
            Scientific review powered by <TI color={T.PINK} /> + Aᴳ
          </p>
        </div>
      </main>
    </div>
  );
}
